import React, { useState, useEffect } from 'react';
import { Handshake, TrendingDown, Clock, DollarSign, Target, BarChart3, CheckCircle, XCircle, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { adminAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Link } from 'react-router-dom';

export default function DealHistoryPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAPI.getDealHistory().then(res => { setData(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#b8ff00] border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-zinc-500">No data</div>;

  const s = data.summary;
  const statusPie = [
    { name: 'Deals Made', value: s.deals_made, fill: '#10b981' },
    { name: 'Closed (No Deal)', value: s.closed_no_deal, fill: '#ef4444' },
    { name: 'Active', value: s.active, fill: '#b8ff00' },
  ].filter(d => d.value > 0);

  return (
    <div className="min-h-screen bg-zinc-50 pb-8">
      <section className="bg-white border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center">
              <Handshake className="text-white" size={28} />
            </div>
            <div>
              <h1 className="font-display font-bold text-2xl text-zinc-900" data-testid="deal-history-title">Deal History & Analytics</h1>
              <p className="text-zinc-500 text-sm">Negotiation patterns and deal insights</p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { icon: Handshake, label: 'Total Rooms', value: s.total_rooms, color: 'bg-violet-100 text-violet-600' },
            { icon: CheckCircle, label: 'Deals Made', value: s.deals_made, color: 'bg-green-100 text-green-600' },
            { icon: Target, label: 'Conversion', value: `${s.conversion_rate}%`, color: 'bg-blue-100 text-blue-600' },
            { icon: TrendingDown, label: 'Avg Discount', value: `${s.avg_discount_pct}%`, color: 'bg-amber-100 text-amber-600' },
            { icon: Clock, label: 'Avg Time to Deal', value: `${s.avg_time_to_deal_hours}h`, color: 'bg-cyan-100 text-cyan-600' },
          ].map(({ icon: Icon, label, value, color }) => (
            <motion.div key={label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-zinc-200 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}><Icon size={16} /></div>
                <span className="text-xs text-zinc-500 uppercase">{label}</span>
              </div>
              <p className="font-display font-bold text-2xl text-zinc-900">{value}</p>
            </motion.div>
          ))}
        </div>

        {/* Deal Volume & Funnel */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-zinc-200 rounded-2xl p-6">
            <h3 className="font-display font-semibold mb-2">Deal Volume</h3>
            <p className="text-sm text-zinc-500 mb-4">Total negotiated value: <strong>{formatCurrency(s.total_deal_volume)}</strong> | Avg deal: <strong>{formatCurrency(s.avg_deal_value)}</strong></p>
            {statusPie.length > 0 ? (
              <div className="flex items-center gap-6">
                <ResponsiveContainer width="50%" height={160}>
                  <PieChart><Pie data={statusPie} dataKey="value" cx="50%" cy="50%" outerRadius={60} strokeWidth={2}>
                    {statusPie.map((e, i) => <Cell key={i} fill={e.fill} />)}
                  </Pie><Tooltip /></PieChart>
                </ResponsiveContainer>
                <div className="space-y-2">
                  {statusPie.map((d, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.fill }} />
                      <span className="text-sm text-zinc-600">{d.name}: <strong>{d.value}</strong></span>
                    </div>
                  ))}
                </div>
              </div>
            ) : <p className="text-zinc-400 text-center py-8">No deals yet</p>}
          </div>

          {/* Most Negotiated */}
          <div className="bg-white border border-zinc-200 rounded-2xl p-6">
            <h3 className="font-display font-semibold mb-4">Most Negotiated Products</h3>
            {data.most_negotiated.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={data.most_negotiated}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#8b5cf6" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-zinc-400 text-center py-8">No data yet</p>}
          </div>
        </div>

        {/* Recent Deals Table */}
        <div className="bg-white border border-zinc-200 rounded-2xl p-6">
          <h3 className="font-display font-semibold mb-4">Recent Negotiations</h3>
          {data.recent_deals.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-zinc-200 text-left text-zinc-500">
                  <th className="pb-3 pr-4">Item</th><th className="pb-3 pr-4">Listed</th><th className="pb-3 pr-4">Deal Price</th>
                  <th className="pb-3 pr-4">Discount</th><th className="pb-3 pr-4">Buyer</th><th className="pb-3 pr-4">Seller</th><th className="pb-3">Status</th>
                </tr></thead>
                <tbody>
                  {data.recent_deals.map((d, i) => {
                    const discount = d.listing_price && d.deal_price ? ((d.listing_price - d.deal_price) / d.listing_price * 100).toFixed(1) : '-';
                    const statusColor = d.status === 'deal_made' ? 'text-green-600 bg-green-50' : d.status === 'active' ? 'text-amber-600 bg-amber-50' : 'text-red-600 bg-red-50';
                    return (
                      <tr key={i} className="border-b border-zinc-100">
                        <td className="py-3 pr-4 font-medium text-zinc-900">{d.item_name}</td>
                        <td className="py-3 pr-4 font-mono">{formatCurrency(d.listing_price || 0)}</td>
                        <td className="py-3 pr-4 font-mono font-bold">{d.deal_price ? formatCurrency(d.deal_price) : '-'}</td>
                        <td className="py-3 pr-4">{discount !== '-' ? <span className="text-green-600 font-medium">{discount}% off</span> : '-'}</td>
                        <td className="py-3 pr-4 text-zinc-600">{d.buyer_name}</td>
                        <td className="py-3 pr-4 text-zinc-600">{d.seller_name}</td>
                        <td className="py-3"><span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColor}`}>{d.status?.replace('_', ' ')}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : <p className="text-zinc-400 text-center py-8">No negotiations yet. <Link to="/marketplace" className="text-violet-500 underline">Browse marketplace</Link> to start!</p>}
        </div>
      </div>
    </div>
  );
}
